<template>
  <ticket-edr-info-base-component
    :ticket-details="ticketDetails"
    :hide-edr-link="hideEdrLink"
    :hide-device-table-link="hideDeviceTableLink"
    @device-link-click="onDeviceLinkClick"
    @edr-process-link-click="onEdrProcessLinkClick"
  />
</template>

<script setup lang="ts" generic="T extends TicketDetails">
import { computed } from "vue";
import { useRouter } from "vue-router";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { isMspAccessRestricted } from "@/_helpers/msp-permissions.ts";
import { RouteName } from "@/constants/routes";
import { SubscriptionModule } from "@/constants/workplaces";
import { SearchSuggestion } from "@/constants/general";
import { AppContext } from "@/types";
import TicketEdrInfoBaseComponent from "./base-components/TicketEdrInfoBaseComponent.vue";
import { isAccessRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

const props = withDefaults(
  defineProps<{
    ticketDetails: T;
    context: AppContext;
    isSoc?: boolean;
  }>(),
  {
    isSoc: false,
  }
);

const router = useRouter();

const isMspContext = computed(() => props.context === AppContext.MSP);

/**
 * Helper to select the correct permission checking function based on the context.
 * This avoids duplicating the ternary logic.
 */
const checkPermission = isMspContext.value ? isMspAccessRestricted : isAccessRestricted;

const hideEdrLink = computed(() =>
  checkPermission(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR)
);

const hideDeviceTableLink = computed(() =>
  checkPermission(RolePermissionsScope.VIEWS, "devicesView")
);

const onDeviceLinkClick = () => {
  const searchQuery = `${SearchSuggestion.TICKET_ID}${props.ticketDetails.eventId}`;

  if (isMspContext.value) {
    router.push({
      name: RouteName.MSP_DEVICES_TAB,
      query: {
        search: searchQuery,
      },
    });
    return;
  }

  if (props.isSoc && props.ticketDetails.workspaceId) {
    router.push({
      name: RouteName.DEVICES,
      query: {
        search: searchQuery,
        workspaceId: props.ticketDetails.workspaceId,
      },
    });
    return;
  }

  router.push({
    name: RouteName.DEVICES,
    query: {
      search: searchQuery,
    },
  });
};

const onEdrProcessLinkClick = () => {
  const searchQuery = `${SearchSuggestion.PROCESS_HASH}${props.ticketDetails?.sections?.edrMetaData?.processHash}`;

  if (isMspContext.value || (props.isSoc && props.ticketDetails.workspaceId)) {
    router.push({
      name: RouteName.EDR_PROCESSES_TAB,
      query: {
        search: searchQuery,
        workspaceId: props.ticketDetails.workspaceId,
      },
    });
    return;
  }

  router.push({
    name: RouteName.EDR_PROCESSES_TAB,
    query: {
      search: searchQuery,
    },
  });
};
</script>
